import {
	Box,
	Table,
	AsyncSelect,
	Inline,
	joinAttributes,
	Stack,
	Text,
	useBreakpoints,
	Icons,
	Time,
	Grid,
	Col,
	Divider,
	ContentToggle,
	Dropdown,
} from "@sembark-travel/ui/base"
import {
	useSearch,
	ListView,
	Search,
	TSearchParams,
	areAdvancedFiltersAppliedDefault,
} from "@sembark-travel/ui/list"
import {
	ButtonLink,
	Link,
	NavLink,
	useLocationQuery,
} from "@sembark-travel/ui/router"
import { useXHR } from "@sembark-travel/xhr"
import React, { Fragment, useMemo, useRef, useState } from "react"
import { Omit } from "utility-types"
import { generatePath } from "../router-utils"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import { AddHotelDialog } from "./NewItem"
import { IHotel, hotelsXHR as XHR } from "./store"
import { useEffect } from "react"
import {
	DateRangePickerField,
	isTruthy,
	SelectField,
	SelectInputField,
	SwitchInputField,
} from "@sembark-travel/ui/form"
import { IRoomType, SelectRoomTypes } from "../RoomTypes"
import { IMealPlan, SelectMealPlans } from "../MealPlans"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	localOrUtcTimeToLocalDate,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import { SelectHotelLocation } from "./Locations"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { TemporaryStorage } from "../TemporaryStorage"
import { IUser, SelectUsers } from "../Users"

type TFilters = TSearchParams & {
	expire_after?: string | Date
	expire_before?: string | Date
	last_updated_after?: string | Date
	last_updated_before?: string | Date
	last_updated_by?: Array<IUser>
	disabled_only?: 1 | 0
	room_types?: Array<IRoomType>
	meal_plans?: Array<IMealPlan>
	star_categories?: Array<{ id: string; name: string }>
	uncategorized?: 1 | 0
	locations?: Array<{ id: string; name: string }>
	trip_destinations?: Array<TTripDestination>
	entry_type?: "manually" | "by_sheet"
}

type TFiltersInLocationQuery = TSearchParams & {
	expire_after?: string
	expire_before?: string
	last_updated_after?: string
	last_updated_before?: string
	disabled_only?: 1 | 0
	room_types?: Array<string>
	meal_plans?: Array<string>
	star_categories?: Array<string>
	uncategorized?: 1
	locations?: Array<string>
	trip_destinations?: Array<string>
	last_updated_by?: Array<string>
	entry_type?: string
}

function filtersToQuery(filters: TFilters): TFiltersInLocationQuery {
	const {
		q,
		page,
		disabled_only,
		room_types,
		meal_plans,
		star_categories,
		uncategorized,
		locations,
		trip_destinations,
		expire_after,
		expire_before,
		last_updated_after,
		last_updated_before,
		entry_type,
		last_updated_by,
	} = filters
	const query: TFiltersInLocationQuery = {}
	if (q) {
		query.q = q
	}
	if (page) {
		query.page = page
	}
	if (expire_after) {
		query.expire_after = dateToQuery(expire_after)
	}
	if (expire_before) {
		query.expire_before = dateToQuery(expire_before)
	}
	if (last_updated_after) {
		query.last_updated_after = dateToQuery(last_updated_after)
	}
	if (last_updated_before) {
		query.last_updated_before = dateToQuery(last_updated_before)
	}
	if (disabled_only) {
		query.disabled_only = 1
	}
	if (uncategorized) {
		query.uncategorized = 1
	}
	if (room_types?.length) {
		query.room_types = room_types.map((s) => `${s.id}_${s.name}`)
	}
	if (meal_plans?.length) {
		query.meal_plans = meal_plans.map((s) => `${s.id}_${s.name}`)
	}
	if (star_categories?.length) {
		query.star_categories = star_categories.map((s) => `${s.id}####${s.name}`)
	}
	if (locations?.length) {
		query.locations = locations.map((s) => `${s.id}_${s.name}`)
	}
	if (trip_destinations?.length) {
		query.trip_destinations = trip_destinations.map((s) => `${s.id}_${s.name}`)
	}
	if (last_updated_by?.length) {
		query.last_updated_by = last_updated_by.map((s) => `${s.id}_${s.name}`)
	}
	if (entry_type) query.entry_type = entry_type
	return query
}

function queryToFilters(query: TFiltersInLocationQuery): TFilters {
	const {
		q,
		page,
		disabled_only,
		room_types,
		meal_plans,
		star_categories,
		uncategorized,
		locations,
		trip_destinations,
		expire_after,
		expire_before,
		last_updated_after,
		last_updated_before,
		entry_type,
		last_updated_by,
	} = query
	const filters: TFilters = {}
	if (q) {
		filters.q = q
	}
	if (page) {
		filters.page = page
	}
	if (expire_after) filters.expire_after = parseDateFromQuery(expire_after)
	if (expire_before) filters.expire_before = parseDateFromQuery(expire_before)
	if (last_updated_after)
		filters.last_updated_after = parseDateFromQuery(last_updated_after)
	if (last_updated_before)
		filters.last_updated_before = parseDateFromQuery(last_updated_before)
	if (entry_type)
		filters.entry_type = entry_type === "manually" ? "manually" : "by_sheet"
	if (disabled_only) {
		filters.disabled_only = 1
	}
	if (uncategorized) {
		filters.uncategorized = 1
	}
	if (star_categories?.length) {
		filters.star_categories = star_categories.map((s: string) => {
			const [id, ...name] = s.split("####")
			return {
				id: isNaN(parseInt(id)) ? id : parseInt(id),
				name: name.join("_"),
			}
		}) as unknown as TFilters["star_categories"]
	}
	if (room_types?.length) {
		filters.room_types = room_types.map((s: string) => {
			const [id, ...name] = s.split("_")
			return {
				id: isNaN(parseInt(id)) ? id : parseInt(id),
				name: name.join("_"),
			}
		}) as unknown as TFilters["room_types"]
	}

	if (meal_plans?.length) {
		filters.meal_plans = meal_plans.map((s: string) => {
			const [id, ...name] = s.split("_")
			return {
				id: isNaN(parseInt(id)) ? id : parseInt(id),
				name: name.join("_"),
			}
		}) as unknown as TFilters["meal_plans"]
	}
	if (locations?.length) {
		filters.locations = locations.map((s: string) => {
			const [id, ...name] = s.split("_")
			return {
				id: isNaN(parseInt(id)) ? id : parseInt(id),
				name: name.join("_"),
			}
		}) as unknown as TFilters["locations"]
	}
	if (trip_destinations && trip_destinations.length) {
		filters.trip_destinations = trip_destinations.map((d) => {
			const slices = d.split("_")
			return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<TTripDestination>
	}
	if (last_updated_by && last_updated_by.length) {
		filters.last_updated_by = last_updated_by.map((d) => {
			const slices = d.split("_")
			return { id: parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<IUser>
	}
	return filters
}
function filtersToAPIParams(params: TFilters) {
	return {
		...params,
		expire_after: params.expire_after
			? dateToUTCString(startOf(params.expire_after, "day"))
			: null,
		expire_before: params.expire_before
			? dateToUTCString(endOf(params.expire_before, "day"))
			: null,
		last_updated_after: params.last_updated_after
			? dateToUTCString(startOf(params.last_updated_after, "day"))
			: null,
		last_updated_before: params.last_updated_before
			? dateToUTCString(endOf(params.last_updated_before, "day"))
			: null,
		disabled_only: isTruthy(params.disabled_only) ? 1 : null,
		room_types: params.room_types?.map((r) => r.id),
		meal_plans: params.meal_plans?.map((r) => r.id),
		star_categories: params.star_categories?.map((r) => r.id),
		uncategorized: isTruthy(params.uncategorized) ? 1 : null,
		trip_destinations: params.trip_destinations?.map((t) => t.id),
		last_updated_by: params.last_updated_by?.map((u) => u.id),
		locations: params.locations?.map((c) => c.id),
		entry_type: params.entry_type,
	}
}
export function HotelsList() {
	const { xs } = useBreakpoints()
	const { hasPermission } = useCheckPermissions()
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInLocationQuery>(
		{
			toQuery: filtersToQuery,
			fromQuery: queryToFilters,
		}
	)
	const [params, setParams] = useSearch<TFilters>(query)
	const isOtherFilterPresent = useMemo(() => {
		return Object.keys(params).some((key) => key !== "page")
	}, [params])
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(params) => {
					setParams({ ...params, page: 1 })
				}}
				title="Hotels"
				actions={
					<Inline gap="2">
						<ButtonLink to={generatePath("/hotels/new")}>Add New</ButtonLink>
						<Dropdown alignRight="sm">
							<Dropdown.ToggleButton level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<NavLink to={generatePath("/hotel-groups")}>
									Hotel Groups
								</NavLink>
								{hasPermission(PERMISSIONS.UPLOAD_BULK_HOTELS) ? (
									<>
										<NavLink to={generatePath("/meal-plans")}>
											Meal Plans
										</NavLink>
										<NavLink to={generatePath("/room-types")}>
											Room Types
										</NavLink>
										<NavLink to={generatePath("/hotel-notes")}>
											General Hotels Notes
										</NavLink>
									</>
								) : null}
								{hasPermission(PERMISSIONS.VIEW_HOTEL_BOOKINGS) ? (
									<NavLink to={generatePath("/hotel-payment-preferences")}>
										Payment Preferences
									</NavLink>
								) : null}
								{hasPermission(PERMISSIONS.UPLOAD_BULK_HOTELS) &&
								isOtherFilterPresent ? (
									<TemporaryStorage
										initUrl={"/hotels-download"}
										initParams={{
											...filtersToAPIParams(params),
										}}
									>
										{({ generate, url, message, reset }) => (
											<Dropdown.MenuItem
												onClick={!url ? generate : () => reset()}
												href={url || "#"}
												target={url ? "_blank" : undefined}
											>
												{message || (
													<>
														<Icons.DocumentDownload /> Download Hotel Details
													</>
												)}
											</Dropdown.MenuItem>
										)}
									</TemporaryStorage>
								) : null}
								{hasPermission(PERMISSIONS.UPLOAD_BULK_HOTELS) ? (
									<>
										<Dropdown.MenuItemDivider />
										<NavLink to={generatePath("/hotels/merge")}>
											Merge Hotels
										</NavLink>
									</>
								) : null}
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
				}
				Filters={Filters}
				areAdvancedFiltersApplied={(params) => {
					const { disabled_only, uncategorized, ...otherParams } = params
					return (
						isTruthy(disabled_only) ||
						isTruthy(uncategorized) ||
						areAdvancedFiltersAppliedDefault(otherParams)
					)
				}}
			>
				<ListView<IHotel, TFilters>
					pageKey="/hotels-listing"
					fetch={(xhr, params) => {
						return XHR(xhr).getHotelsWithTotal({
							...filtersToAPIParams(params),
							include:
								"includes,payment_preference,created_by,rate_uploaded_by,thumbs",
						})
					}}
					params={params}
					onPageChange={(page) => setParams({ ...params, page })}
				>
					{({ items: hotels }) =>
						xs ? (
							<HotelsCardView hotels={hotels} />
						) : (
							<HotelsTableView hotels={hotels} />
						)
					}
				</ListView>
			</Search>
		</Fragment>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectField
				select={SelectTripDestination}
				name="trip_destinations"
				label="Destinations"
				multiple
				fetchOnMount
			/>
			<SelectField
				name="locations"
				select={SelectHotelLocation}
				label="Locations"
				required
				multiple
			/>
			<SelectField
				select={SelectRoomTypes}
				name="room_types"
				label="Room Types"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectMealPlans}
				name="meal_plans"
				label="Meal Plans"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectHotelStarCategory}
				name="star_categories"
				label="Star Categories"
				multiple
			/>
			<DateRangePickerField
				label="Last Updated Between"
				fromName="last_updated_after"
				toName="last_updated_before"
			/>
			<SelectField
				select={SelectUsers}
				name="last_updated_by"
				label="Last Updated By"
				multiple
			/>
			<SelectInputField name="entry_type" label="Entry Method" required>
				<option key="Select a type" value="">
					Select a Type...
				</option>
				<option key="manually" value="manually">
					Added Manually
				</option>
				<option key="added_by_sheet" value="by_sheet">
					Added by Sheet
				</option>
			</SelectInputField>
			<DateRangePickerField
				label="Rate Expiring-In"
				fromName="expire_after"
				toName="expire_before"
			/>
			<SwitchInputField name="uncategorized" label="Include Uncategorized" />
			<SwitchInputField name="disabled_only" label="Disabled Only" />
		</Stack>
	)
}

export function HotelsTableView({ hotels }: { hotels: Array<IHotel> }) {
	return (
		<Table
			responsive
			bordered
			hover
			headers={[
				"Name",
				"Meal Plans",
				"Room Type",
				"Check-In/Out",
				"Rate Validity",
				"Child EB Age",
				"Payment Preference",
			]}
			rows={hotels.map((hotel) => [
				<Inline gap="2" maxWidth={"xs"}>
					<Link
						to={hotel.id.toString()}
						color="accent"
						fontWeight="semibold"
						anchored
					>
						{hotel.thumb_image_url ? (
							<Box width="12" height="12">
								<Box
									as="img"
									src={hotel.thumb_image_url}
									alt="Display"
									width="12"
									height="12"
									borderWidth="1"
									rounded="md"
									style={{ objectFit: "cover" }}
								/>
							</Box>
						) : (
							<Box
								display="flex"
								width="12"
								height="12"
								alignItems="center"
								justifyContent="center"
								bgColor="primary"
								color="primary"
								borderWidth="1"
								borderColor="primary"
								rounded="md"
							>
								<Icons.Bed size="6" style={{ color: "#b5c2ea" }} />
							</Box>
						)}
					</Link>

					<Stack>
						<Link
							to={hotel.id.toString()}
							color="accent"
							fontWeight="semibold"
							anchored
						>
							<Box>{hotel.name}</Box>
						</Link>
						<Box color="muted">
							<Stack>
								<Inline gap={"1"} alignItems={"center"} flexWrap={"wrap"}>
									<Inline gap="1" alignItems={"start"}>
										{hotel.location.short_name}
									</Inline>
									<Text color={"muted"} opacity="70">
										•
									</Text>
									{hotel.stars ? (
										<>
											<Inline alignItems={"start"}>
												{hotel.stars}{" "}
												{!isNaN(parseInt(hotel.stars)) &&
												/^\d+$/.test(hotel.stars) ? (
													<Box>
														<Icons.Star size="4" style={{ color: "#FFC53D" }} />
													</Box>
												) : null}
											</Inline>
											<Text color={"muted"} opacity="70">
												•
											</Text>
										</>
									) : null}

									{hotel.entry_type === "manually" ? (
										<Icons.HandPencil />
									) : (
										<Icons.Sheet />
									)}
								</Inline>
								<Inline>
									{hotel.group ? (
										<Link
											to={generatePath("/hotel-groups/:groupId", {
												groupId: hotel.group.id.toString(),
											})}
											color="muted"
										>
											{hotel.group.name}
										</Link>
									) : null}
								</Inline>
							</Stack>
						</Box>
					</Stack>
				</Inline>,
				<Inline
					gap="2"
					flexWrap={"wrap"}
					style={{ maxWidth: "10rem", minWidth: "5rem" }}
				>
					{hotel.meal_plans.map((mealPlan) => mealPlan.name).join(" • ")}
				</Inline>,
				<Box
					style={{
						minWidth: "10rem",
						maxWidth: "15rem",
						wordBreak: "break-all",
					}}
					flexWrap={"wrap"}
				>
					<ContentToggle data={hotel.room_types} />
				</Box>,
				<Stack>
					<Inline gap={"1"} alignItems="center">
						<Text whiteSpace="nowrap">
							{formatDate(
								localOrUtcTimeToLocalDate(
									hotel.checkin_at_local,
									hotel.checkin_at
								),
								"HH:mm"
							)}
						</Text>
						-
						<Text whiteSpace="nowrap">
							{formatDate(
								localOrUtcTimeToLocalDate(
									hotel.checkout_at_local,
									hotel.checkout_at
								),
								"HH:mm"
							)}
						</Text>
					</Inline>
				</Stack>,
				<Stack>
					{hotel.rate_expiry_date ? (
						<>
							<Time
								timestamp={hotel.rate_expiry_date}
								localTimestamp={hotel.rate_expiry_date_local ?? undefined}
								format="DD MMM, YYYY"
								whiteSpace="nowrap"
							/>
							<Inline gap={"1"} alignItems="center">
								<Box>
									<Icons.User />
								</Box>
								<Text color={"muted"} whiteSpace="nowrap">
									{hotel.rate_uploaded_by?.name ?? "You"}
								</Text>
							</Inline>
						</>
					) : null}
				</Stack>,
				<Box>
					{hotel.extra_bed_child_age_start}-{hotel.extra_bed_child_age_end}
					yo
				</Box>,
				<Box>
					{hotel.payment_preference?.breakdowns ? (
						<ContentToggle data={hotel.payment_preference.breakdowns} />
					) : (
						<Text color="muted">N/A</Text>
					)}
				</Box>,
			])}
		/>
	)
}

export function HotelsCardView({ hotels }: { hotels: Array<IHotel> }) {
	return (
		<Stack as="ol" gap="4">
			{hotels.map((hotel) => (
				<Stack
					as="li"
					key={hotel.id}
					bgColor="default"
					rounded="lg"
					boxShadow="base"
					padding="4"
					gap="2"
				>
					<Inline gap="2">
						<Link
							to={hotel.id.toString()}
							color="accent"
							fontWeight="semibold"
							anchored
						>
							{hotel.thumb_image_url ? (
								<Box width="12" height="12">
									<Box
										as="img"
										src={hotel.thumb_image_url}
										alt="Display"
										width="12"
										height="12"
										borderWidth="1"
										rounded="md"
										style={{ objectFit: "cover" }}
									/>
								</Box>
							) : (
								<Box
									display="flex"
									width="12"
									height="12"
									alignItems="center"
									justifyContent="center"
									bgColor="primary"
									color="primary"
									borderWidth="1"
									borderColor="primary"
									rounded="md"
								>
									<Icons.Bed size="6" style={{ color: "#8DA4EF" }} />
								</Box>
							)}
						</Link>

						<Stack>
							<Link
								to={hotel.id.toString()}
								color="accent"
								fontWeight="semibold"
								anchored
							>
								<Box>{hotel.name}</Box>
							</Link>
							<Box color="muted">
								<Stack>
									<Inline alignItems={"center"} gap={"1"} flexWrap={"wrap"}>
										<Inline gap={"1"} alignItems={"center"} flexWrap={"wrap"}>
											<Inline gap="1" alignItems={"start"}>
												{hotel.location.short_name}
											</Inline>
											<Text color={"muted"} opacity="70">
												•
											</Text>
											{hotel.stars ? (
												<>
													<Inline alignItems={"start"}>
														{hotel.stars}{" "}
														{!isNaN(parseInt(hotel.stars)) &&
														/^\d+$/.test(hotel.stars) ? (
															<Box>
																<Icons.Star
																	size="4"
																	style={{ color: "#FFC53D" }}
																/>
															</Box>
														) : null}
													</Inline>
													<Text color={"muted"} opacity="70">
														•
													</Text>
												</>
											) : null}

											{hotel.entry_type === "manually" ? (
												<Icons.HandPencil />
											) : (
												<Icons.Sheet />
											)}
										</Inline>
									</Inline>

									{hotel.group ? (
										<Link
											to={generatePath("/hotel-groups/:groupId", {
												groupId: hotel.group.id.toString(),
											})}
											color="muted"
										>
											{hotel.group.name}
										</Link>
									) : null}
								</Stack>
							</Box>
						</Stack>
					</Inline>

					<Stack>
						<Grid gap={"1"}>
							<Col xs={8}>
								<Stack gap={"1"}>
									<Inline gap="1" alignItems="center">
										<Icons.Meal />
										<Text>Meal Plan</Text>
									</Inline>
									<Inline gap="2" flexWrap={"wrap"}>
										{hotel.meal_plans
											.map((mealPlan) => mealPlan.name)
											.join(" • ")}
									</Inline>
								</Stack>
							</Col>
							<Col>
								<Stack gap={"1"}>
									<Inline gap="1" alignItems="center">
										<Icons.Clock />
										<Text>CheckIn/Out</Text>
									</Inline>
									<Inline gap={"1"} alignItems="center">
										<Text>
											{formatDate(
												localOrUtcTimeToLocalDate(
													hotel.checkin_at_local,
													hotel.checkin_at
												),
												"HH:mm"
											)}
										</Text>
										-
										<Text>
											{formatDate(
												localOrUtcTimeToLocalDate(
													hotel.checkout_at_local,
													hotel.checkout_at
												),
												"HH:mm"
											)}
										</Text>
									</Inline>
								</Stack>
							</Col>
						</Grid>
						<Stack>
							<Stack gap={"1"}>
								<Inline gap="1" alignItems="center">
									<Icons.Bed />
									<Text>Room Types</Text>
								</Inline>
								{hotel.room_types.map((roomType) => roomType.name).join(" • ")}
							</Stack>
						</Stack>
					</Stack>

					<Divider sm />
					<Grid gap={"1"}>
						<Col xs={8}>
							<Stack gap={"1"}>
								<Inline gap="1" alignItems="center">
									<Box>
										<Icons.Calendar />
									</Box>
									<Text>Rate Validity</Text>
								</Inline>
								<Stack>
									{hotel.rate_expiry_date ? (
										<>
											<Time
												timestamp={hotel.rate_expiry_date}
												localTimestamp={
													hotel.rate_expiry_date_local ?? undefined
												}
												format="DD MMM, YYYY"
												whiteSpace="nowrap"
											/>
											<Inline gap={"1"} alignItems="center">
												<Box>
													<Icons.User />
												</Box>
												<Text color={"muted"}>
													{hotel.rate_uploaded_by?.name ?? "You"}
												</Text>
											</Inline>
										</>
									) : null}
								</Stack>
							</Stack>
						</Col>
						<Col>
							<Stack gap={"1"}>
								<Inline gap="1" alignItems="center">
									<Box>
										<Icons.Users />
									</Box>
									<Text>Child EB Age</Text>
								</Inline>
								<Inline alignItems={"center"}>
									{hotel.extra_bed_child_age_start}-
									{hotel.extra_bed_child_age_end}
									yo
								</Inline>
							</Stack>
						</Col>
					</Grid>
					<Stack>
						<Stack gap={"1"}>
							<Inline gap="1" alignItems="center">
								<Box>
									<Icons.Calendar />
								</Box>
								<Text>Payment Preference</Text>
							</Inline>
							<Box>
								{hotel.payment_preference?.breakdowns ? (
									hotel.payment_preference.breakdowns
										?.map((breakdown) => breakdown.name)
										.join(" • ")
								) : (
									<Text color="muted">N/A</Text>
								)}
							</Box>
						</Stack>
					</Stack>
				</Stack>
			))}
		</Stack>
	)
}

export function SelectHotels({
	tripDestinations,
	showInfo = true,
	showGlobalHotelsNotes = false,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	tripDestinations?: Array<TTripDestination>
	showInfo?: boolean
	showGlobalHotelsNotes?: boolean
}) {
	const [newName, setNewName] = useState("")
	const xhr = useXHR()
	const promiseOfCreation = useRef<((hotel: IHotel) => void) | undefined>(
		undefined
	)
	return (
		<Fragment>
			<AsyncSelect
				multiple
				onCreateNew={(query: string) => {
					setNewName(query)
					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
				optionRenderer={({ option, created }) =>
					created ? (
						<Box>Add new Hotel "{option.name}"</Box>
					) : (
						<Box>
							<Box>{option.name}</Box>
							{option.location || option.stars_string ? (
								<Box fontSize="sm" color="muted">
									{joinAttributes(
										option.location.short_name,
										option.stars_string,
										option.entry_type === "by_sheet" ? (
											<Icons.Sheet />
										) : (
											<Icons.HandPencil />
										)
									)}
								</Box>
							) : null}
						</Box>
					)
				}
				{...props}
				perFetchLimit={15}
				fetch={(q, { page }) =>
					XHR(xhr)
						.getHotels({
							limit: 15,
							page,
							q,
							include: showGlobalHotelsNotes
								? "notes,general_hotel_notes"
								: "notes",
							trip_destinations: tripDestinations?.length
								? tripDestinations.map((t) => t.id)
								: null,
						})
						.then((resp) => {
							if (
								showGlobalHotelsNotes &&
								(resp.meta.general_hotel_notes?.length || 0) > 0
							) {
								return resp.data.map((hotel) => ({
									global_notes: resp.meta.general_hotel_notes,
									...hotel,
								}))
							} else {
								return resp.data
							}
						})
				}
			/>
			{showInfo && !props.multiple && props.value ? (
				<Text fontSize="sm" color="muted">
					{joinAttributes(
						props.value?.location?.short_name,
						props.value.stars_string,
						props.value.entry_type === "by_sheet" ? (
							<Icons.Sheet
								title={`Added by Sheet ${props.value?.created_by?.name ? `\n by ${props.value?.created_by?.name}` : ""}`}
							/>
						) : (
							<Icons.HandPencil
								title={`Added  Manually ${props.value?.created_by?.name ? `\n by ${props.value?.created_by?.name}` : ""}`}
							/>
						)
					)}
				</Text>
			) : null}
			<AddHotelDialog
				open={!!newName}
				initialValues={{
					name: newName,
					trip_destinations: tripDestinations,
				}}
				onClose={() => {
					setNewName("")
				}}
				onSuccess={(hotel) => {
					promiseOfCreation.current?.(hotel)
				}}
			/>
		</Fragment>
	)
}

export function SelectSelfBookHotel({
	tripDestinations,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	tripDestinations?: Array<TTripDestination>
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			creatable
			multiple
			{...props}
			optionRenderer={({ option, created }) =>
				created ? (
					<Box>Add accommodation "{option.name}"</Box>
				) : (
					<Box>
						<Box>{option.name}</Box>
						{option.location || option.stars_string ? (
							<Box fontSize="sm" color="muted">
								{joinAttributes(
									option.location.short_name,
									option.stars_string
								)}
							</Box>
						) : null}
					</Box>
				)
			}
			perFetchLimit={15}
			fetch={(q, { page }) =>
				XHR(xhr)
					.getHotels({
						limit: 15,
						page,
						q,
						trip_destinations: tripDestinations?.length
							? tripDestinations.map((t) => t.id)
							: null,
					})
					.then((resp) => resp.data)
			}
		/>
	)
}

export function SelectHotelStarCategory({
	tripDestinations,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	tripDestinations?: Array<TTripDestination>
}) {
	const xhr = useXHR()
	return (
		<Fragment>
			<AsyncSelect
				{...props}
				cacheKey={`hotel-stars-${tripDestinations?.map((t) => t.id).join("-")}`}
				fetch={(q) =>
					XHR(xhr).getHotelStarCategories({
						q,
						trip_destinations: tripDestinations?.length
							? tripDestinations.map((t) => t.id)
							: null,
					})
				}
			/>
		</Fragment>
	)
}
